import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { CreditSubmission } from '../../../../api/creditSubmissionApi';
import { formatCurrency } from './utils';

interface DetailSection11Props {
  submission: CreditSubmission;
}

export default function DetailSection11({ submission }: DetailSection11Props) {
  if (
    !submission.financial_projection ||
    Object.keys(submission.financial_projection).length === 0
  ) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>11. Proyeksi Keuangan</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Profil Usaha */}
          {(submission.financial_projection.business_profile ||
            submission.financial_projection.economic_sector) && (
            <div className="border-b pb-4">
              <h4 className="font-semibold mb-3 text-gray-700">Profil Usaha</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {submission.financial_projection.business_profile && (
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-600">Profil Calon Debitur</label>
                    <p className="mt-1">{submission.financial_projection.business_profile}</p>
                  </div>
                )}
                {submission.financial_projection.economic_sector && (
                  <div>
                    <label className="text-sm text-gray-600">Sektor Ekonomi</label>
                    <p className="font-medium mt-1">
                      {submission.financial_projection.economic_sector}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Kebutuhan Dana */}
          {(submission.financial_projection.loan_purpose ||
            submission.financial_projection.purpose_category ||
            submission.financial_projection.kredit_usulan) && (
            <div className="border-b pb-4">
              <h4 className="font-semibold mb-3 text-gray-700">Kebutuhan Dana</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {submission.financial_projection.loan_purpose && (
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-600">Tujuan Pinjaman</label>
                    <p className="mt-1">{submission.financial_projection.loan_purpose}</p>
                  </div>
                )}
                {submission.financial_projection.kredit_usulan && (
                  <div>
                    <label className="text-sm text-gray-600">Nominal Dana yang Dibutuhkan</label>
                    <p className="font-medium mt-1 text-lg">
                      {formatCurrency(submission.financial_projection.kredit_usulan)}
                    </p>
                  </div>
                )}
                {submission.financial_projection.purpose_category && (
                  <div>
                    <label className="text-sm text-gray-600">Kategori Tujuan Pinjaman</label>
                    <p className="font-medium mt-1">
                      {submission.financial_projection.purpose_category}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Asumsi & Ketentuan */}
          {(submission.financial_projection.repayment_capacity_pct !== undefined ||
            submission.financial_projection.suku_bunga_flat !== undefined ||
            submission.financial_projection.interest_type ||
            submission.financial_projection.repayment_method) && (
            <div className="border-b pb-4">
              <h4 className="font-semibold mb-3 text-gray-700">Asumsi & Ketentuan</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {submission.financial_projection.repayment_capacity_pct !== undefined &&
                  submission.financial_projection.repayment_capacity_pct !== null && (
                    <div>
                      <label className="text-sm text-gray-600">Repayment Capacity (%)</label>
                      <p className="font-medium mt-1">
                        {submission.financial_projection.repayment_capacity_pct}%
                      </p>
                    </div>
                  )}
                {submission.financial_projection.suku_bunga_flat !== undefined &&
                  submission.financial_projection.suku_bunga_flat !== null && (
                    <div>
                      <label className="text-sm text-gray-600">Suku Bunga Flat (%)</label>
                      <p className="font-medium mt-1">
                        {submission.financial_projection.suku_bunga_flat}%
                      </p>
                    </div>
                  )}
                {submission.financial_projection.interest_type && (
                  <div>
                    <label className="text-sm text-gray-600">Jenis Bunga / Biaya Jasa</label>
                    <p className="font-medium mt-1">
                      {submission.financial_projection.interest_type === 'flat'
                        ? 'Flat'
                        : 'Efektif'}
                    </p>
                  </div>
                )}
                {submission.financial_projection.repayment_method && (
                  <div>
                    <label className="text-sm text-gray-600">Metode Angsuran</label>
                    <p className="font-medium mt-1">
                      {submission.financial_projection.repayment_method === 'flat'
                        ? 'Bulanan (Pokok dan Bunga)'
                        : submission.financial_projection.repayment_method === 'annuity'
                          ? 'Anuitas'
                          : submission.financial_projection.repayment_method === 'effective'
                            ? 'Efektif Menurun'
                            : submission.financial_projection.repayment_method}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Saldo Awal Kas & Bank */}
          {(submission.financial_projection.kas_before !== undefined ||
            submission.financial_projection.tabungan_before !== undefined ||
            submission.financial_projection.giro_before !== undefined) && (
            <div className="border-b pb-4">
              <h4 className="font-semibold mb-3 text-gray-700">
                Saldo Awal Kas & Bank (Sebelum Pencairan)
              </h4>
              <div className="grid md:grid-cols-3 gap-4">
                {submission.financial_projection.kas_before !== undefined &&
                  submission.financial_projection.kas_before !== null && (
                    <div>
                      <label className="text-sm text-gray-600">Kas</label>
                      <p className="font-medium mt-1">
                        {formatCurrency(submission.financial_projection.kas_before)}
                      </p>
                    </div>
                  )}
                {submission.financial_projection.tabungan_before !== undefined &&
                  submission.financial_projection.tabungan_before !== null && (
                    <div>
                      <label className="text-sm text-gray-600">Tabungan</label>
                      <p className="font-medium mt-1">
                        {formatCurrency(submission.financial_projection.tabungan_before)}
                      </p>
                    </div>
                  )}
                {submission.financial_projection.giro_before !== undefined &&
                  submission.financial_projection.giro_before !== null && (
                    <div>
                      <label className="text-sm text-gray-600">Giro</label>
                      <p className="font-medium mt-1">
                        {formatCurrency(submission.financial_projection.giro_before)}
                      </p>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Usulan Pinjaman */}
          {(submission.financial_projection.kredit_usulan ||
            submission.financial_projection.tenor_bulan) && (
            <div className="border-b pb-4">
              <h4 className="font-semibold mb-3 text-gray-700">Usulan Pinjaman</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {submission.financial_projection.kredit_usulan && (
                  <div>
                    <label className="text-sm text-gray-600">Plafon</label>
                    <p className="font-medium mt-1 text-lg">
                      {formatCurrency(submission.financial_projection.kredit_usulan)}
                    </p>
                  </div>
                )}
                {submission.financial_projection.tenor_bulan && (
                  <div>
                    <label className="text-sm text-gray-600">Tenor (Bulan)</label>
                    <p className="font-medium mt-1">
                      {submission.financial_projection.tenor_bulan} bulan
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Calculated Values */}
          {submission.financial_projection.calculated &&
            Object.keys(submission.financial_projection.calculated).length > 0 && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-700">Hasil Perhitungan</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  {submission.financial_projection.calculated.angsuranPerBulan && (
                    <div>
                      <label className="text-sm text-gray-600">Angsuran per Bulan</label>
                      <p className="font-bold text-lg mt-1 text-blue-600">
                        {formatCurrency(
                          submission.financial_projection.calculated.angsuranPerBulan
                        )}
                      </p>
                    </div>
                  )}
                  {submission.financial_projection.calculated.saldoAkhirKasAfter && (
                    <div>
                      <label className="text-sm text-gray-600">Saldo Akhir Kas & Bank</label>
                      <p className="font-bold text-lg mt-1 text-green-600">
                        {formatCurrency(
                          submission.financial_projection.calculated.saldoAkhirKasAfter
                        )}
                      </p>
                    </div>
                  )}
                  {submission.financial_projection.calculated.arusKasBersihAfter && (
                    <div>
                      <label className="text-sm text-gray-600">Arus Kas Bersih</label>
                      <p className="font-bold text-lg mt-1 text-purple-600">
                        {formatCurrency(
                          submission.financial_projection.calculated.arusKasBersihAfter
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </CardContent>
    </Card>
  );
}
